import cn from "classnames"
import { useCallback, useLayoutEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"

import { useGetAdrState } from "@/api/useIncidents"
import { Button, Flex, Text } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { TAB_KEYS_BY_PAGE } from "@/const/tab.constants"
import * as AdrDomain from "@/domain/adr"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { FilterOutlined, NoFound } from "@/icons"
import { CreateActionEmptyTable } from "@/molecules/EmptyTable"
import {
  useAdrIncidentsList,
  useEnvironmentsForAdrIncident
} from "@/pages/TriagePage/application/useAdrIncidents"
import { RuleList } from "@/pages/TriagePage/components/pages/AdrManagementPage/components/organisms/DetailsSection"
import { IncidentCard } from "@/pages/TriagePage/components/pages/AdrManagementPage/components/organisms/IncidentCard"
import { IncidentsColumn } from "@/pages/TriagePage/components/pages/AdrManagementPage/components/organisms/IncidentsColumn"
import { TAB_KEY } from "@/pages/TriagePage/const/filters.constants"
import { useFlash } from "@/pages/TriagePage/hooks/useFlash"
import { FiltersTabAdrManagement } from "../FiltersTabAdrManagement"
import { NoIncidents } from "../NoIncidents"
import { Loading } from "./Loading"

import styles from "./TabAdrManagement.module.scss"

import type { AdrIncident } from "@/domain/adr"
import type { TabAdrManagementProps } from "./TabAdrManagement.types"

export const TabAdrManagement = (props: TabAdrManagementProps) => {
  const { column, config, isActive, parentId, showTeaser } = props
  const context = useAdrIncidentsList(config)
  const { response, isLoading, isFetched, isError, noIncidents } = context
  const isFlash = useFlash(context)
  const { response: adrStateResponse, isLoading: isAdrStateLoading } =
    useGetAdrState(config)
  const isAdrActive = AdrDomain.isActiveState(adrStateResponse?.data?.state)
  const contextEnvironment = useEnvironmentsForAdrIncident(config)
  const { isLoading: isLoadingEnvironment } = contextEnvironment
  const loading = isLoading || isLoadingEnvironment || isAdrStateLoading

  const { t } = useTranslation()
  const { data } = response || {}
  const { list } = data || {}

  const { ROOT, INCIDENTS } = NAMES_ROUTES.TRIAGE
  const navigate = useNavigate()
  const { logger, EVENTS } = useLogger()

  const openKey = TAB_KEYS_BY_PAGE.INCIDENTS[0]
  const dismissedKey = TAB_KEYS_BY_PAGE.INCIDENTS[1]

  const onOpenIncident = (incident: AdrIncident) => {
    const { id, type, priority, dismissDetails } = incident
    const status = dismissDetails ? dismissedKey : openKey
    const payload = { id, type, priority, status }

    logger.info(EVENTS.ANALYTIC_EVENTS.ADR.NAVIGATE_INCIDENT_PAGE, { payload })
    navigate(`/${ROOT}/${INCIDENTS}/${id}`)
  }

  const commonProps = {
    isFlash: isActive && !!isFlash,
    onOpenIncident
  }

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const setRef = useCallback(() => {
    setPortalTarget(
      document
        .getElementById(parentId)
        ?.querySelector(".ant-tabs-nav-list") as HTMLDivElement
    )
  }, [parentId])
  const [filterOpen, setFilterOpen] = useState(false)

  useLayoutEffect(() => {
    if (!loading)
      document
        .getElementById(parentId)
        ?.setAttribute("data-tab-adr-management", "false")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const showFilterButton = isActive && portalTarget
  const showData = !loading && isAdrActive && !noIncidents
  const showList = showData && !column
  const emptyList = showList && (list?.length === 0 || isError)
  const typeNoIncidents = !column ? "dismissed" : undefined
  const adrState = adrStateResponse?.data?.state
  const isLearningState = AdrDomain.isLearningState(adrState)
  const isPrerequisitesDegraded =
    AdrDomain.isPrerequisitesDegradedState(adrState)
  const isPrerequisitesNotMet = AdrDomain.isPrerequisitesNotMetState(adrState)
  const showNoIncidents = !loading && noIncidents && isAdrActive
  const showAllDisabled = !loading && isPrerequisitesDegraded
  const showLoading = loading || isLearningState || isPrerequisitesNotMet
  const showMore = context.response?.metadata?.hasNext && showData
  const type = isLearningState ? "learning" : "inactive"

  return (
    <div className={styles.wrapper} ref={setRef}>
      {showLoading && <Loading isLoading={loading || isLearningState} />}
      {showNoIncidents && <NoIncidents type={typeNoIncidents} />}
      {showAllDisabled && <NoIncidents type="disabled" />}
      {showTeaser && (
        <NoIncidents
          type={type}
          extra={
            type === "inactive" && (
              <Flex vertical gap={12} align="center">
                <Text type="secondary">
                  {t("triage.adr.management.empty.inactive.rules")}
                </Text>
                <RuleList config={config} />
              </Flex>
            )
          }
        />
      )}
      <FiltersTabAdrManagement open={filterOpen} config={config} />
      <div
        className={cn(
          styles.container,
          styles.wrapper,
          column ? styles.containerGrid : styles.containerFlex,
          filterOpen && styles.containerFilter
        )}
      >
        {showData && column && (
          <>
            <IncidentsColumn priority="urgent" data={data} {...commonProps} />
            <IncidentsColumn priority="warning" data={data} {...commonProps} />
            <IncidentsColumn priority="notice" data={data} {...commonProps} />
          </>
        )}
        {showList && !emptyList && (
          <Flex vertical className={styles.list}>
            {list?.map((incident) => (
              <IncidentCard
                inline
                key={incident.id}
                className={`${incident.priority}-theme`}
                incident={incident}
                onOpen={onOpenIncident}
              />
            ))}
          </Flex>
        )}
        {showList && emptyList && (
          <CreateActionEmptyTable
            tKey=""
            image={<NoFound />}
            message={t("filter.emptyState.message")}
            action={t("filter.emptyState.clear")}
            to={`/${ROOT}/${INCIDENTS}?${TAB_KEY}=${dismissedKey}`}
          />
        )}
        {showFilterButton &&
          createPortal(
            <Flex flex={1} justify="end" align="center">
              <Button
                size="small"
                disabled={!isFetched || noIncidents}
                type={filterOpen ? "link" : "text"}
                icon={<FilterOutlined />}
                onClick={() => setFilterOpen((prev) => !prev)}
              >
                {t("general.filters")}
              </Button>
            </Flex>,
            portalTarget
          )}
      </div>
      {showMore && (
        <Flex flex={1} justify="center">
          <Button
            type="primary"
            loading={context.isFetchingNextPage}
            disabled={context.hasNextPage === false}
            onClick={() => context.fetchNextPage()}
          >
            {t("general.showMore")}
          </Button>
        </Flex>
      )}
    </div>
  )
}
